import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import swal from 'sweetalert';
import { Pagination } from 'semantic-ui-react';
import {
	getBookableJourneysAdmin,
	deleteBookableJourney,
	updateBookableJourney,
	bulkUpdateBookableJourneyAvailability,
} from '../../../api/bookableJourneyApi';
import { isAllYearAvailability, seasonsLabel, monthsForPicker, isChannelClosed } from '../../../utils/bookingAvailability';
import JourneyAvailabilityModal from '../cms/JourneyAvailabilityModal';
import '../../../styles/admin-cms.scss';

function activeBadge(active) {
	const cls = active ? 'admin-cms-status admin-cms-status--published' : 'admin-cms-status admin-cms-status--draft';
	return <span className={cls}>{active ? 'Active' : 'Inactive'}</span>;
}

function channelBadge(row, channel) {
	const enabled = channel === 'group' ? row.group_enabled !== false : row.private_enabled !== false;
	const label = channel === 'group' ? 'Group' : 'Private';
	if (!enabled) return <span className="text-muted">—</span>;
	if (isChannelClosed(row, channel)) {
		return (
			<span className="badge badge-secondary" title="Enabled in catalog but closed for booking">
				{label} · closed
			</span>
		);
	}
	return <span className="badge badge-success">{label}</span>;
}

function formatPrice(row) {
	if (row.vendor_total_price == null || row.vendor_total_price === '') return '—';
	const cc = row.currency_code || 'USD';
	return `${cc} ${Number(row.vendor_total_price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function availabilityBadgeClass(row, channel) {
	if (isChannelClosed(row, channel)) return ' admin-cms-availability-badge--closed';
	if (!isAllYearAvailability(row, channel)) return ' admin-cms-availability-badge--seasonal';
	return '';
}

function availabilityLabel(row, channel) {
	if (channel === 'group') return row.group_available_months_label || seasonsLabel(row, 'group');
	return row.private_available_months_label || seasonsLabel(row, 'private');
}

export default function BookableJourneysAdminList() {
	const history = useHistory();
	const location = useLocation();
	const [rows, setRows] = useState([]);
	const [meta, setMeta] = useState({});
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [channelFilter, setChannelFilter] = useState('all');
	const [availabilityFilter, setAvailabilityFilter] = useState('all');
	const [selected, setSelected] = useState({});
	const [savingMonths, setSavingMonths] = useState(false);
	const [togglingSlug, setTogglingSlug] = useState(null);
	const [modal, setModal] = useState(null);

	const fetchRows = useCallback((page = 1) => {
		setLoading(true);
		const params = { page, admin_list: 'true', ...(search ? { search } : {}) };
		if (channelFilter === 'group') params.group_enabled = 'true';
		if (channelFilter === 'private') params.private_enabled = 'true';
		if (channelFilter === 'inactive') params.active = 'false';
		getBookableJourneysAdmin(params)
			.then((res) => {
				const data = res.data || {};
				setRows(Array.isArray(data.bookable_journeys) ? data.bookable_journeys : []);
				setMeta(data.meta || {});
			})
			.catch(() => setRows([]))
			.finally(() => setLoading(false));
	}, [search, channelFilter]);

	useEffect(() => {
		const page = new URLSearchParams(location.search).get('page') || 1;
		fetchRows(Number(page));
	}, [fetchRows, location.search]);

	const filteredRows = useMemo(() => {
		if (availabilityFilter === 'seasonal') {
			return rows.filter((row) => !isAllYearAvailability(row));
		}
		if (availabilityFilter === 'all_year') {
			return rows.filter((row) => isAllYearAvailability(row));
		}
		return rows;
	}, [rows, availabilityFilter]);

	const selectedSlugs = useMemo(
		() => Object.keys(selected).filter((slug) => selected[slug]),
		[selected]
	);

	const toggleRow = (slug, checked) => {
		setSelected((s) => {
			const next = { ...s };
			if (checked) next[slug] = true;
			else delete next[slug];
			return next;
		});
	};

	const toggleAll = (checked) => {
		if (!checked) {
			setSelected({});
		} else {
			const next = {};
			filteredRows.forEach((row) => { next[row.slug] = true; });
			setSelected(next);
		}
	};

	const openModalForRows = (targetRows, title, subtitle, preferredChannel = null) => {
		const row = targetRows[0];
		const slugs = targetRows.map((r) => r.slug);
		const groupEnabled = targetRows.every((r) => r.group_enabled !== false);
		const privateEnabled = targetRows.every((r) => r.private_enabled !== false);
		setModal({
			slugs,
			title,
			subtitle,
			groupEnabled,
			privateEnabled,
			initialChannel: preferredChannel,
			groupMode: row?.group_availability_mode || 'all_year',
			privateMode: row?.private_availability_mode || 'all_year',
			initialGroupMonths: slugs.length === 1 ? monthsForPicker(row, 'group') : [...Array.from({ length: 12 }, (_, i) => i + 1)],
			initialPrivateMonths: slugs.length === 1 ? monthsForPicker(row, 'private') : [...Array.from({ length: 12 }, (_, i) => i + 1)],
		});
	};

	const saveAvailability = (payload) => {
		if (!modal?.slugs?.length) return;
		const channel = payload?.channel || 'both';
		const months = payload?.months ?? payload;
		setSavingMonths(true);
		bulkUpdateBookableJourneyAvailability(modal.slugs, months, channel)
			.then(() => {
				swal('Saved', `Availability updated for ${modal.slugs.length} item${modal.slugs.length !== 1 ? 's' : ''}.`, 'success');
				setModal(null);
				setSelected({});
				fetchRows(Number(new URLSearchParams(location.search).get('page') || 1));
			})
			.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not save availability', 'error'))
			.finally(() => setSavingMonths(false));
	};

	const handleToggleActive = (row, active) => {
		swal({
			title: active ? 'Show in dropdowns?' : 'Stop showing in UI?',
			text: active
				? `"${row.title}" will appear in group/private booking dropdowns again.`
				: `"${row.title}" will be hidden from booking dropdowns. You can turn it back on anytime.`,
			icon: 'warning',
			buttons: ['Cancel', active ? 'Show' : 'Stop'],
			dangerMode: !active,
		}).then((ok) => {
			if (!ok) return;
			setTogglingSlug(row.slug);
			updateBookableJourney(row.slug, { active })
				.then(() => {
					swal(active ? 'Active' : 'Stopped', active ? 'Journey is visible in booking dropdowns.' : 'Hidden from booking dropdowns.', 'success');
					setSelected((s) => {
						const next = { ...s };
						delete next[row.slug];
						return next;
					});
					fetchRows(Number(new URLSearchParams(location.search).get('page') || 1));
				})
				.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not update', 'error'))
				.finally(() => setTogglingSlug(null));
		});
	};

	const handleStopSelected = () => {
		const targetRows = filteredRows.filter((r) => selected[r.slug]);
		if (!targetRows.length) return;
		swal({
			title: `Stop ${targetRows.length} item${targetRows.length !== 1 ? 's' : ''}?`,
			text: 'Selected journeys will be hidden from group and private booking dropdowns.',
			icon: 'warning',
			buttons: ['Cancel', 'Stop selected'],
			dangerMode: true,
		}).then((ok) => {
			if (!ok) return;
			setTogglingSlug('bulk');
			Promise.all(targetRows.map((row) => updateBookableJourney(row.slug, { active: false })))
				.then(() => {
					swal('Stopped', `${targetRows.length} item${targetRows.length !== 1 ? 's' : ''} hidden from dropdowns.`, 'success');
					setSelected({});
					fetchRows(Number(new URLSearchParams(location.search).get('page') || 1));
				})
				.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not update some items', 'error'))
				.finally(() => setTogglingSlug(null));
		});
	};

	const handleDelete = (row) => {
		swal({
			title: 'Remove from booking catalog?',
			text: 'This removes the dropdown entry. Guided journey content (if any) is unchanged.',
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then((ok) => {
			if (!ok) return;
			deleteBookableJourney(row.slug)
				.then(() => {
					swal('Removed', '', 'success');
					fetchRows();
				})
				.catch((err) => swal('Error', err.response?.data?.errors?.[0] || 'Could not delete', 'error'));
		});
	};

	const pag = meta.pagination || meta || {};
	const totalPages = pag.total_pages || 1;
	const currentPage = pag.current_page || 1;

	return (
		<div className="admin-cms-list">
			<Link to="/admin/booking_calendar" className="admin-cms-back">← Booking</Link>
			<header className="admin-cms-list__head">
				<div>
					<h1>Booking catalog</h1>
					<p>
						Manage what appears in group departure and private tour dropdowns — title, pricing, months, and channels.
						Separate from guided journey content.
					</p>
				</div>
				<Link to="/admin/booking_catalog/new" className="btn btn-primary">+ Add bookable journey</Link>
			</header>

			<div className="admin-cms-list__filters admin-cms-list__filters--wrap">
				<input
					type="search"
					placeholder="Search by title or slug…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && fetchRows(1)}
				/>
				<select
					className="form-control form-control-sm admin-cms-list__select"
					value={channelFilter}
					onChange={(e) => setChannelFilter(e.target.value)}
					aria-label="Filter by channel"
				>
					<option value="all">All channels</option>
					<option value="group">Group only</option>
					<option value="private">Private only</option>
					<option value="inactive">Inactive</option>
				</select>
				<select
					className="form-control form-control-sm admin-cms-list__select"
					value={availabilityFilter}
					onChange={(e) => setAvailabilityFilter(e.target.value)}
					aria-label="Filter by availability"
				>
					<option value="all">All availability</option>
					<option value="seasonal">Seasonal only</option>
					<option value="all_year">All year only</option>
				</select>
				<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => fetchRows(1)}>Search</button>
			</div>

			{selectedSlugs.length > 0 && (
				<div className="admin-cms-bulk-bar">
					<span>{selectedSlugs.length} selected</span>
					<button
						type="button"
						className="btn btn-sm btn-primary"
						onClick={() => {
							const targetRows = filteredRows.filter((r) => selected[r.slug]);
							openModalForRows(
								targetRows,
								`Set availability (${targetRows.length} items)`,
								'Same months will apply to all selected items.'
							);
						}}
					>
						Set months for selected
					</button>
					<button
						type="button"
						className="btn btn-sm btn-outline-warning"
						onClick={handleStopSelected}
						disabled={togglingSlug === 'bulk'}
					>
						Stop selected
					</button>
					<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSelected({})}>
						Clear selection
					</button>
				</div>
			)}

			{loading ? (
				<p className="admin-cms-muted">Loading…</p>
			) : (
				<div className="table-responsive">
					<table className="table admin-cms-table">
						<thead>
							<tr>
								<th className="admin-cms-table__check">
									<input
										type="checkbox"
										className="admin-cms-table__checkbox"
										checked={filteredRows.length > 0 && selectedSlugs.length === filteredRows.length}
										onChange={(e) => toggleAll(e.target.checked)}
										aria-label="Select all on this page"
									/>
								</th>
								<th>Title</th>
								<th>Vendor price</th>
								<th>Group</th>
								<th>Private</th>
								<th>Availability</th>
								<th>Status</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{filteredRows.length === 0 && (
								<tr><td colSpan={8} className="admin-cms-muted">No items match this filter.</td></tr>
							)}
							{filteredRows.map((row) => (
									<tr key={row.slug} className={selected[row.slug] ? 'admin-cms-table__row--selected' : ''}>
										<td className="admin-cms-table__check">
											<input
												type="checkbox"
												className="admin-cms-table__checkbox"
												checked={!!selected[row.slug]}
												onChange={(e) => toggleRow(row.slug, e.target.checked)}
												aria-label={`Select ${row.title}`}
											/>
										</td>
										<td>
											<strong>{row.title}</strong>
											<br />
											<small className="text-muted">{row.slug}</small>
											{row.guided_journey_slug && row.guided_journey_slug !== row.slug && (
												<>
													<br />
													<small className="text-muted">Guide: {row.guided_journey_slug}</small>
												</>
											)}
										</td>
										<td>{formatPrice(row)}</td>
										<td>{channelBadge(row, 'group')}</td>
										<td>{channelBadge(row, 'private')}</td>
										<td>
											<div className="admin-cms-availability-stack">
												{row.group_enabled !== false && (
													<button
														type="button"
														className={`admin-cms-availability-badge${availabilityBadgeClass(row, 'group')}`}
														onClick={() => openModalForRows([row], `Availability: ${row.title}`, `${row.slug} — group departures`, 'group')}
														title="Click to change group months"
													>
														<span className="admin-cms-availability-badge__channel">Group</span>
														{availabilityLabel(row, 'group')}
													</button>
												)}
												{row.private_enabled !== false && (
													<button
														type="button"
														className={`admin-cms-availability-badge${availabilityBadgeClass(row, 'private')}`}
														onClick={() => openModalForRows([row], `Availability: ${row.title}`, `${row.slug} — private tours`, 'private')}
														title="Click to change private months"
													>
														<span className="admin-cms-availability-badge__channel">Private</span>
														{availabilityLabel(row, 'private')}
													</button>
												)}
											</div>
										</td>
										<td>{activeBadge(row.active)}</td>
										<td className="admin-cms-table__actions">
											<Link to={`/admin/booking_catalog/${row.slug}`} className="btn btn-sm btn-outline-primary">Edit</Link>
											{row.active !== false ? (
												<button
													type="button"
													className="btn btn-sm btn-outline-warning"
													disabled={togglingSlug === row.slug}
													onClick={() => handleToggleActive(row, false)}
												>
													Stop
												</button>
											) : (
												<button
													type="button"
													className="btn btn-sm btn-outline-success"
													disabled={togglingSlug === row.slug}
													onClick={() => handleToggleActive(row, true)}
												>
													Show
												</button>
											)}
											<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)}>Remove</button>
										</td>
									</tr>
								))}
						</tbody>
					</table>
				</div>
			)}

			{totalPages > 1 && (
				<Pagination
					boundaryRange={1}
					siblingRange={1}
					activePage={currentPage}
					totalPages={totalPages}
					onPageChange={(e, { activePage }) => {
						history.push(`${location.pathname}?page=${activePage}`);
					}}
				/>
			)}

			<JourneyAvailabilityModal
				key={modal?.slugs?.join(',') || 'closed'}
				open={!!modal}
				title={modal?.title || 'Availability'}
				subtitle={modal?.subtitle}
				groupEnabled={modal?.groupEnabled !== false}
				privateEnabled={modal?.privateEnabled !== false}
				initialGroupMonths={modal?.initialGroupMonths || []}
				initialPrivateMonths={modal?.initialPrivateMonths || []}
				groupMode={modal?.groupMode || 'all_year'}
				privateMode={modal?.privateMode || 'all_year'}
				initialChannel={modal?.initialChannel}
				saving={savingMonths}
				onCancel={() => setModal(null)}
				onSave={saveAvailability}
			/>
		</div>
	);
}
